import React, { useRef, useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Mic, Stop } from '@mui/icons-material';
import { toast } from 'react-toastify';
import http from '../../services/http';

// Escolhe um formato de gravação suportado pelo navegador e aceito pelo Whisper.
const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  const candidates = ['audio/webm', 'audio/ogg', 'audio/mp4'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const extFromMime = (mime) => {
  if (!mime) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'mp4';
  return 'webm';
};

/**
 * Botão de ditado por voz: grava o áudio, envia para /transcribe (Whisper) e
 * devolve o texto transcrito via onTranscribed. Reúso simples de MediaRecorder.
 */
const VoiceTranscribeButton = ({ onTranscribed, disabled = false, size = 'small' }) => {
  const [status, setStatus] = useState('idle'); // idle | recording | transcribing
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const handleStop = async (mimeType) => {
    stopStream();
    const type = mimeType || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];

    if (!blob.size) {
      setStatus('idle');
      return;
    }

    setStatus('transcribing');
    try {
      const form = new FormData();
      form.append('audio', blob, `gravacao.${extFromMime(type)}`);
      const res = await http.post('/transcribe', form, { timeout: 120000 });
      const text = (res.data?.text || '').trim();
      if (text) {
        onTranscribed(text);
      } else {
        toast.info('Não foi possível identificar fala no áudio.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao transcrever o áudio.');
    } finally {
      setStatus('idle');
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Gravação de áudio não suportada neste navegador.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => handleStop(recorder.mimeType);
      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus('recording');
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      toast.error('Não foi possível acessar o microfone.');
      stopStream();
      setStatus('idle');
    }
  };

  const handleClick = () => {
    if (status === 'idle') {
      startRecording();
    } else if (status === 'recording' && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  const tooltip =
    status === 'recording'
      ? 'Parar e transcrever'
      : status === 'transcribing'
        ? 'Transcrevendo...'
        : 'Ditar por voz';

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <span>
        <IconButton
          size={size}
          onClick={handleClick}
          disabled={disabled || status === 'transcribing'}
          color={status === 'recording' ? 'error' : 'default'}
          aria-label="Ditar por voz"
          sx={
            status === 'recording'
              ? {
                  animation: 'voicePulse 1.4s ease-in-out infinite',
                  '@keyframes voicePulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.45 },
                  },
                }
              : undefined
          }
        >
          {status === 'transcribing' ? (
            <CircularProgress size={18} />
          ) : status === 'recording' ? (
            <Stop />
          ) : (
            <Mic />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default VoiceTranscribeButton;
